import React, { useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Widget, Text } from '@synergy/atomics_and_hooks';
import Theme from '../../styles/Theme/Theme';
import useWidgetData from '../../utils/hooks/useWidgetData';

/**
 * CustomWidgetRenderer
 *
 * Renders a custom widget inside the Zone display pages using the
 * @synergy/atomics_and_hooks Widget component (same as the Buildings app).
 *
 * It reads the widget's `meta` (all fields from the user-configurations API),
 * fetches metric data via useWidgetData, serialises it into the format the
 * Widget component expects, and delegates chart rendering to @synergy/charts.
 */

/* ── Chart-type kept as-is (the Widget passes it to @synergy/charts) ── */
const CHART_TYPE_MAP = {
  columnsChartApex: 'columnsChartApex',
  barChartApex: 'barChartApex',
  mixedLineColumnChartApex: 'mixedLineColumnChartApex',
  lineChartApex: 'lineChartApex',
  areaChartApex: 'areaChartApex',
  pieChart: 'pieChart',
  donutChart: 'donutChart',
  gaugeChart: 'gaugeChart',
  radarChart: 'radarChart',
  heatmapChart: 'heatmapChart',
  treemapChart: 'treemapChart',
  // Direct ApexCharts names → Buildings equivalents
  bar: 'columnsChartApex',
  line: 'lineChartApex',
  area: 'areaChartApex',
  pie: 'pieChart',
  donut: 'donutChart',
  radialBar: 'gaugeChart',
  radar: 'radarChart',
  heatmap: 'heatmapChart',
  treemap: 'treemapChart',
};

/* ── Styled helpers for the customInfo bar ── */

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-left: 4px;
`;

const Badge = styled.span`
  background: ${Theme.color?.['teal-3'] || '#6FC2B4'};
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoContainer = styled.div`
  margin: 0 ${Theme.spacing?.['s06'] || '12px'} ${Theme.spacing?.['s03'] || '6px'};
  padding: ${Theme.spacing?.['s03'] || '6px'} ${Theme.spacing?.['s04'] || '8px'};
  background: ${Theme.color?.['neutral-0.5'] || '#F5F8FA'};
  border: 1px solid ${Theme.color?.['coolgray-4'] || '#BBBCBC'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 8px;
  container-type: inline-size;
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const InfoLabel = styled(Text)`
  color: ${Theme.color?.['coolgray-10'] || '#63666A'};
  font-size: 11px;
  font-weight: 600;
`;

const InfoValue = styled(Text)`
  color: ${Theme.color?.['teal-4'] || '#00ABAB'};
  font-size: 11px;
  font-weight: 600;
  background: ${Theme.color?.['teal-0.5'] || '#E2EEE9'};
  border-radius: 10px;
  padding: 2px 8px 3px 8px;
  margin-left: 4px;
`;

const PeriodValue = styled(Text)`
  color: ${Theme.color?.['blue-3'] || '#00A3E0'};
  font-size: 11px;
  font-weight: 600;
  background: ${Theme.color?.['blue-0.5'] || '#E3F5FF'};
  border-radius: 10px;
  padding: 2px 8px 3px 8px;
  margin-left: 4px;
`;

const Separator = styled.div`
  width: 0.5px;
  height: 20px;
  background-color: ${Theme.color?.['coolgray-2'] || '#D0D0CE'};

  @container (max-width: 400px) {
    display: none;
  }
`;

const DateLabel = styled(Text)`
  color: ${Theme.color?.['coolgray-10'] || '#63666A'};
  font-size: 11px;
  font-weight: 600;
`;

const DateValue = styled(Text)`
  color: ${Theme.color?.['blue-3'] || '#00A3E0'};
  font-size: 11px;
  font-weight: 600;
  background: ${Theme.color?.['blue-0.5'] || '#E3F5FF'};
  border-radius: 10px;
  padding: 2px 8px 3px 8px;
  margin-left: 6px;
`;

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  row-gap: 4px;

  > div {
    display: flex;
    align-items: center;
  }
`;

/**
 * Serialise useWidgetData output into the shape expected by @synergy/charts
 * (the internal chart renderer used by Widget).
 *
 * Expected shape:
 *   { series, categories, unit, tooltip, columnWidth, filename,
 *     headerValue, headerCategory }
 */
const serializeChartData = (series, labels) => {
  if (!series || (Array.isArray(series) && series.length === 0)) return null;

  // For pie / donut / radialBar — series is a flat number array
  if (Array.isArray(series) && typeof series[0] === 'number') {
    return { series, categories: labels };
  }

  // For cartesian charts — series is [{ name, data }]
  return {
    series,
    categories: labels,
    tooltip: { enabled: true },
    columnWidth: '60%',
    filename: '',
  };
};

/**
 * Main renderer component.
 *
 * @param {{ widget: object }} props
 *   widget.meta — all raw fields from the user-configurations API
 *   widget.name, widget.class — identification
 */
const CustomWidgetRenderer = ({ widget }) => {
  const meta = useMemo(() => widget?.meta || {}, [widget?.meta]);
  const rawChartType = meta.chartType || 'columnsChartApex';
  const chartType = CHART_TYPE_MAP[rawChartType] || rawChartType;

  // Fetch real metric data from middleware
  const { series: fetchedSeries, labels: fetchedLabels, loading, refetch } = useWidgetData(meta);

  const dateSelectionMode = meta.dateSelectionMode;
  const periodicity = meta.period || 'DAILY';

  const refetchFirstItem = useCallback(() => {
    refetch(true);
  }, [refetch]);

  // Auto-refresh rolling-period widgets
  useEffect(() => {
    if (dateSelectionMode === 'rollingPeriod') {
      const refreshInterval = periodicity === 'RAW'
        ? 60 * 1000
        : 60 * 60 * 1000;
      const intervalId = setInterval(() => {
        refetchFirstItem();
      }, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [dateSelectionMode, refetchFirstItem, periodicity]);

  // Labels / categories
  const labels = useMemo(() => {
    if (fetchedLabels.length > 0) return fetchedLabels;
    return meta.labels || meta.categories || [];
  }, [fetchedLabels, meta]);

  // Serialise chart data for the Widget component
  const chartData = useMemo(
    () => serializeChartData(fetchedSeries, labels),
    [fetchedSeries, labels],
  );

  // Date range display
  const { startDate, endDate } = useMemo(() => {
    const dates = meta.dates || {};
    if (dateSelectionMode === 'rollingPeriod' && meta.rollingPeriod) {
      return { startDate: 'Rolling', endDate: 'Now' };
    }
    return { startDate: dates.start || '-', endDate: dates.end || '-' };
  }, [meta, dateSelectionMode]);

  const items = meta.items || [];
  const metrics = meta.metrics || [];

  // ── Build the customInfo JSX (info bar below title) ──
  const customInfoContent = (
    <InfoContainer>
      {metrics.length > 0 && (
        <InfoSection>
          <InfoLabel>Metrics</InfoLabel>
          <BadgesContainer>
            {metrics.map(m => <Badge key={m}>{m}</Badge>)}
          </BadgesContainer>
        </InfoSection>
      )}

      {items.length > 0 && (
        <>
          <Separator />
          <InfoSection>
            <InfoLabel>Items</InfoLabel>
            <InfoValue>{items.length}</InfoValue>
          </InfoSection>
        </>
      )}

      <Separator />
      <InfoSection>
        <InfoLabel>Period</InfoLabel>
        <PeriodValue>{periodicity}</PeriodValue>
      </InfoSection>

      <Separator />
      <DateRangeContainer>
        <div>
          <DateLabel>From</DateLabel>
          <DateValue>{startDate}</DateValue>
        </div>
        <div>
          <DateLabel>To</DateLabel>
          <DateValue>{endDate}</DateValue>
        </div>
      </DateRangeContainer>
    </InfoContainer>
  );

  // ── Build Widget `data` prop — same shape as the Buildings app ──
  const widgetData = useMemo(() => ({
    background: 'white',
    title: meta.name || meta.title || widget?.name || '',
    subtitle: meta.description || '',
    icon: meta.icon || 'BarChart',
    iconColor: 'teal-4',
    border: true,
    activeShadow: false,
    charts: [
      {
        chartType,
        height: '100%',
        isFetching: loading,
        data: chartData,
      },
    ],
  }), [meta, widget?.name, chartType, loading, chartData]);

  return (
    <Widget
      data={widgetData}
      fetchIsLoading={loading}
      customInfo={customInfoContent}
    />
  );
};

export default CustomWidgetRenderer;
